import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import endOfMonth from 'date-fns/endOfMonth';
import formatISO from 'date-fns/formatISO';
import parse from 'date-fns/parse';
import startOfMonth from 'date-fns/startOfMonth';
import subMonths from 'date-fns/subMonths';
import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { Occ } from '../../../../../core/occ';
import { ConverterService } from '../../../../../core/util';
import { STATISTICS_SOLD_TO_NORMALIZER } from '../connectors/converters';
import { StatisticsQueryParams, StatisticsQueryType, StatisticsSoldTo } from '../model';

@Injectable({
  providedIn: 'root',
})
export class StatisticsConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService
  ) {}

  loadSoldTos(userId: string): Observable<StatisticsSoldTo[]> {
    const now = Date.now();
    const url = this.occEndpointsService.buildUrl('statistics', {
      urlParams: { userId, query: StatisticsQueryType.SOLD_TO },
      queryParams: {
        // BW requires these two although they are not really relevant for retrieving sold-tos
        dateFrom: formatISO(startOfMonth(subMonths(now, 12)), { representation: 'date' }),
        dateTo: formatISO(endOfMonth(now), { representation: 'date' }),
      },
    });
    return this.httpClient.get<Occ.StatisticsList>(url).pipe(
      pluck('statistics'),
      map((entries) => entries.filter((entry) => entry.id !== 'SUMME')),
      this.converterService.pipeableMany(STATISTICS_SOLD_TO_NORMALIZER)
    );
  }

  loadOverview(userId: string): Observable<Occ.Statistics[]> {
    const now = Date.now();
    const url = this.occEndpointsService.buildUrl('statistics', {
      urlParams: { userId, query: StatisticsQueryType.CATEGORY },
      queryParams: {
        dateFrom: formatISO(startOfMonth(subMonths(now, 12)), { representation: 'date' }),
        dateTo: formatISO(endOfMonth(now), { representation: 'date' }),
      },
    });
    return this.httpClient.get<Occ.StatisticsList>(url).pipe(pluck('statistics'));
  }

  query(userId: string, query: StatisticsQueryType, params: StatisticsQueryParams): Observable<Occ.Statistics[]> {
    const url = this.occEndpointsService.buildUrl('statistics', {
      urlParams: { userId, query: query },
      queryParams: {
        soldTos: params.soldTos || '',
        categories: params.categories || '',
        dateFrom: formatISO(startOfMonth(parse(params.dateFrom, 'yyyy-MM-dd', new Date())), { representation: 'date' }),
        dateTo: formatISO(endOfMonth(parse(params.dateTo, 'yyyy-MM-dd', new Date())), { representation: 'date' }),
      },
    });
    return this.httpClient.get<Occ.StatisticsList>(url).pipe(
      pluck('statistics'),
      map((entries) => entries.filter((entry) => entry.id !== 'SUMME'))
    );
  }

  downloadXls(userId: string, type: StatisticsQueryType, params: StatisticsQueryParams): Observable<Blob> {
    const url = this.occEndpointsService.buildUrl('statisticsDownload', {
      urlParams: { userId, type },
      queryParams: {
        soldTos: params.soldTos || '',
        categories: params.categories || '',
        dateFrom: formatISO(startOfMonth(parse(params.dateFrom, 'yyyy-MM-dd', new Date())), { representation: 'date' }),
        dateTo: formatISO(endOfMonth(parse(params.dateTo, 'yyyy-MM-dd', new Date())), { representation: 'date' }),
        ...(params.articleFilter ? { articleFilter: params.articleFilter } : {}),
      },
    });
    return this.httpClient.get(url, {
      responseType: 'blob',
    });
  }
}
